import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { get, useForm } from 'react-hook-form';
import { z } from 'zod';
import i18n from '@/Config/app/i18n';
import {
  TOPIC_DESCRIPTION_MAX_LENGTH,
  TOPIC_NAME_MAX_LENGTH,
} from '@/Domain/api/Conversation/ConversationTopics.constants';
import { TopicDto } from '@/Domain/api/Conversation/dto/Topic.dto';
import { useConversationTopicDelete } from '@/Domain/hooks/Conversation/useConversationTopicDelete.hook';
import { useConversationTopicUpdate } from '@/Domain/hooks/Conversation/useConversationTopicUpdate.hook';

const topicsSchema = z.object({
  topic: z
    .string()
    .trim()
    .max(
      TOPIC_NAME_MAX_LENGTH,
      i18n.t('insights_page:manage_topics.form.validation.max_topic', {
        maxLength: TOPIC_NAME_MAX_LENGTH,
      }),
    )
    .min(1, i18n.t('insights_page:manage_topics.form.validation.min_topic')),
  topic_description: z
    .string()
    .trim()
    .max(
      TOPIC_DESCRIPTION_MAX_LENGTH,
      i18n.t('insights_page:manage_topics.form.validation.max_description', {
        maxLength: TOPIC_DESCRIPTION_MAX_LENGTH,
      }),
    )
    .optional(),
});

export type TopicForm = z.infer<typeof topicsSchema>;

const MIN_TOPICS = 1;
export const useTopicForm = (topic: TopicDto) => {
  const [isEditMode, setIsEditMode] = useState(false);

  const { handleUpdateTopic, isPending } = useConversationTopicUpdate();
  const { handleDeleteTopic } = useConversationTopicDelete();

  useEffect(() => {
    if (!topic.topic) {
      setIsEditMode(true);
    }
  }, [topic.topic]);

  const form = useForm<TopicForm>({
    mode: 'onChange',
    values: {
      topic: topic.topic,
      topic_description: topic.topic_description,
    },
    resolver: zodResolver(topicsSchema),
  });

  const handleDelete = (conversationTopicId?: number) => {
    if (conversationTopicId) {
      handleDeleteTopic(conversationTopicId);
    }
  };

  const handleSubmit = async (formTopic: TopicForm) => {
    try {
      await handleUpdateTopic({ ...formTopic, id: topic.id });
      setIsEditMode(false);
    } catch (error) {
      form.setError(`topic`, {
        message: get(error, 'response.data.errors.root[0].message'),
      });
    }
  };

  const handleReset = () => {
    form.reset();
    form.clearErrors();
  };

  return {
    form,
    handleSubmit,
    handleDelete,
    handleReset,
    isEditMode,
    isUpdatePending: isPending,
    setIsEditMode,
    MIN_TOPICS,
  };
};
